import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import type { Role } from '@soc/shared';
import { useAuth } from './useAuth';

/**
 * Gates a route behind authentication and (optionally) a set of roles.
 * super_admin always passes the role check (mirrors the backend RolesGuard).
 */
export function ProtectedRoute({
  children,
  requiredRoles,
}: {
  children: ReactNode;
  requiredRoles?: Role[];
}) {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <AuthSplash />;
  if (!isAuthenticated || !user) return <Navigate to="/login" replace />;

  const roleOk =
    !requiredRoles ||
    requiredRoles.length === 0 ||
    user.role === 'super_admin' ||
    requiredRoles.includes(user.role);
  if (!roleOk) return <Navigate to="/" replace />;

  return <>{children}</>;
}

/** Minimal themed loading state while the session is resolved. */
function AuthSplash() {
  return (
    <div
      className="theme-noc"
      style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--surface-app)',
        color: 'var(--text-muted)',
        fontSize: 13,
        letterSpacing: 1,
      }}
    >
      <span className="mono">AUTHENTICATING…</span>
    </div>
  );
}
